// ============================================================
// ClientAnswer.tsx — (Big-audit fix batch 2, M1) the client's answer to an
// issued statement (adults), moved out of IssuedStatement.tsx:
//   • a MANUAL answer (paper / email / in person / a recorded call): the
//     button → the form → a read-first summary + "Change answer" (as before),
//     now with who recorded it, when, and an optional attachment;
//   • an answer given by E-SIGNATURE can't be changed (server-enforced): no
//     "Change answer" — instead "Record a later instruction" (how + when +
//     what, a reason, and the evidence: a file on this case). It is kept
//     ALONGSIDE the signed answer, waits for an approver who is NOT the person
//     who recorded it (Approve / Refuse… with a reason), and once approved the
//     money follows it;
//   • the answer history: the e-signed answer and every instruction, with who
//     recorded / decided it, when, how and its attachment.
// ============================================================
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  completeFileUploadApi, deleteFileApi, listFilesApi, requestDownloadUrlApi, requestUploadUrlApi,
} from '../../../api/casevoApi'
import {
  CLIENT_APPROVAL_METHOD_LABELS,
  type AnswerHistoryEntry, type ClientApprovalBody, type ClientApprovalMethod, type LaterInstructionBody, type StatementSummary,
} from '../../../types/settlementStatement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, BIcon, INPUT, LABEL, NOTE, FIELD_ERROR, money } from '../../billing/billingUi'
import { PenceField, useBadFields } from '../settlementUi'
import ActionBox from '../../shared/ActionBox'
import ConfirmDialogue from '../../shared/ConfirmDialogue'
import DateInput from '../../shared/DateInput'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import ModalFrame from '../../shared/ModalFrame'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'
import { ukDate } from '../../../utils/civilDate'
import { CHIP, FIGURE_GRID, Figure, Fold, SummaryRow } from './statementBits'

const SELECT = `${INPUT} pr-8`
const QUIET = 'text-[13px] font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2 disabled:opacity-50'
const day = (iso: string | null | undefined): string => (iso ? ukDate(String(iso).slice(0, 10)) : '—')
// A manual answer is never "E-signature" (only the online signing records that).
const MANUAL_METHODS = (Object.keys(CLIENT_APPROVAL_METHOD_LABELS) as ClientApprovalMethod[]).filter((m) => m !== 'E_SIGNATURE')
export const LATER_REASON_MAX = 1000
// (M6) The most the client can dispute: the charges before any credit.
const disputeCapOf = (s: StatementSummary): number => s.figures.chargesBeforeCreditPence ?? s.figures.totalDeductionsPence
const disputeCapText = (s: StatementSummary): string => disputeCapOf(s) > s.figures.totalDeductionsPence
  ? `The disputed amount can’t be more than the charges before the credit (${money(disputeCapOf(s))}).`
  : `The disputed amount can’t be more than the total deductions (${money(disputeCapOf(s))}).`
// (M3) An open e-signed dispute from an earlier version of this statement.
export const carriedDisputeTitle = (d: NonNullable<StatementSummary['carriedDispute']>): string => `Open dispute from version ${d.fromVersion}: ${money(d.disputedPence)}`
export const CARRIED_DISPUTE_TEXT = 'The client disputed this by e-signature, and correcting the statement doesn’t end that. Their answer to this version can only come through a fresh e-signature, or a later instruction approved by a second person.'
export const CarriedDisputeNote: React.FC<{ d: NonNullable<StatementSummary['carriedDispute']> }> = ({ d }) => (
  <div data-carried-dispute="">
    <ActionBox ariaLabel="Open dispute from an earlier version" tone="warn" title={carriedDisputeTitle(d)} message={CARRIED_DISPUTE_TEXT} />
  </div>
)
const answerText = (r: 'AGREED' | 'DISPUTED', disputedPence: number | null): string => (r === 'AGREED' ? 'Agreed the deductions' : `Disputed ${money(disputedPence ?? 0)}`)

// What the later-instruction flow can do (StatementPanel wires the API).
export interface LaterActions {
  record: (body: LaterInstructionBody) => Promise<boolean>
  approve: (instructionId: string) => Promise<boolean>
  refuse: (instructionId: string, reason: string) => Promise<boolean>
}
export const ANSWER_NEEDS_DELIVERY = 'Record how the statement reached the client (email, post or by hand) before recording their answer. They can only agree or dispute a statement they have received.'
export const ESIGN_LOCKED_NOTE_AFTER = 'The client first answered by e-signature, so their answer can’t be changed directly. Record any further instruction the same way — an approver (not you) must approve it before the money follows it.'
export const ESIGN_LOCKED_NOTE = 'The client gave this answer by e-signature, so it can’t be changed. If they have changed their mind, record their later instruction — it is kept alongside the signed answer and an approver (not you) must approve it before the money follows it.'

// ── Open an attachment (a file on the case) in a new tab ──────
const openFile = async (caseId: string, fileId: string): Promise<void> => {
  const w = window.open('', '_blank')
  try {
    const r = await requestDownloadUrlApi(caseId, fileId, true)
    if (w) w.location.href = r.downloadUrl; else window.open(r.downloadUrl, '_blank')
  } catch { w?.close() }
}
const FileLink: React.FC<{ caseId?: string; file: { id: string; filename: string } }> = ({ caseId, file }) => (
  caseId
    ? <button type="button" className="inline-flex items-center gap-[4px] text-[12.5px] font-medium text-casevo-link hover:underline underline-offset-2 focus:outline-none casevo-focus rounded-[4px]"
        onClick={() => { void openFile(caseId, file.id) }}><BIcon name="memo" size={12} />{file.filename}</button>
    : <span className="text-[12.5px]">{file.filename}</span>
)

// ── The attachment: one of the case's documents, or upload one ──
// (The upload files it on the case — the Documents list shows it too.)
type CaseFile = { id: string; filename: string }
export const AttachmentField: React.FC<{ caseId: string; value: string | null; onChange: (id: string | null) => void; required?: boolean; error?: string | null; label?: string }> = ({ caseId, value, onChange, required, error, label = 'Attachment' }) => {
  const id = useId()
  const [files, setFiles] = useState<CaseFile[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)
  const input = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    let live = true
    listFilesApi(caseId)
      .then((fs) => { if (live) setFiles(fs.map((f) => ({ id: f.id, filename: f.filename }))) })
      .catch(() => { if (live) setLoadError('The case’s documents couldn’t be loaded. You can still upload a file.') })
    return () => { live = false }
  }, [caseId])
  const upload = async (file: File): Promise<void> => {
    setUploading(file.name); setLoadError(null)
    let made: string | null = null
    try {
      const p = await requestUploadUrlApi(caseId, { filename: file.name, mimeType: file.type, sizeBytes: file.size })
      made = p.fileId
      const put = await fetch(p.uploadUrl, { method: 'PUT', headers: { 'Content-Type': p.mimeType ?? file.type }, body: file })
      if (!put.ok) throw new Error(`Upload failed (${put.status})`)
      await completeFileUploadApi(p.fileId).catch(() => undefined)
      setFiles((fs) => [{ id: p.fileId, filename: file.name }, ...fs.filter((f) => f.id !== p.fileId)])
      onChange(p.fileId)
    } catch (e) {
      if (made) await deleteFileApi(caseId, made).catch(() => undefined)
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error
      setLoadError(msg || `“${file.name}” couldn’t be uploaded. Please try again.`)
    } finally { setUploading(null) }
  }
  const err = error ?? null
  return (
    <FormField label={label} required={required} variant="info" htmlFor={`${id}-file`} helper="A file on this case — the signed form, the email or the letter.">
      <div className="flex items-center gap-[8px] flex-wrap">
        <select id={`${id}-file`} className={`${SELECT} flex-1 min-w-[200px]`} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}
          aria-invalid={!!err || undefined} aria-describedby={err ? `${id}-file-err` : undefined}>
          <option value="">{files.length ? 'Choose a document…' : 'No documents on the case yet'}</option>
          {files.map((f) => <option key={f.id} value={f.id}>{f.filename}</option>)}
        </select>
        <button type="button" className={B_SECONDARY_SM} disabled={!!uploading} onClick={() => input.current?.click()}>
          <BIcon name="plus" size={12} />{uploading ? 'Uploading…' : 'Upload a file'}
        </button>
        <input ref={input} type="file" className="hidden" aria-hidden="true" tabIndex={-1}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f) }} />
      </div>
      {uploading && <span className={`${NOTE} block mt-[4px]`} role="status">Uploading {uploading}…</span>}
      {loadError && <span className="block mt-[4px] text-[12.5px] text-casevo-warn" role="alert">{loadError}</span>}
      <span id={`${id}-file-err`}><FieldError message={err ?? undefined} /></span>
    </FormField>
  )
}

// ── The answer fields (shared by the manual form and the later instruction) ──
export const ANSWER_REQUIRED = 'Choose what the client said.'
type AnswerFields = { method: ClientApprovalMethod | ''; date: string | null; response: 'AGREED' | 'DISPUTED' | ''; disputed: number | null; comment: string }
// The main button's spot on the statement card: what's sent here sits there, not in
// the form. undefined = no such spot (this renders on its own); null = not mounted yet.
export type MainSlot = HTMLElement | null | undefined
export const inSlot = (slot: MainSlot, node: React.ReactNode): React.ReactNode => (slot === undefined ? node : slot ? createPortal(node, slot) : null)
const useAnswerFields = (s: StatementSummary, start: AnswerFields) => {
  const [f, setF] = useState<AnswerFields>(start)
  const set = <K extends keyof AnswerFields>(k: K, v: AnswerFields[K]): void => setF((x) => ({ ...x, [k]: v }))
  const errs = useMemo(() => ({
    response: !f.response ? ANSWER_REQUIRED : null,
    method: !f.method ? 'Choose how the client gave their answer.' : null,
    date: !f.date ? 'Enter the date the client answered.' : null,
    disputed: f.response === 'DISPUTED' && (!f.disputed || f.disputed <= 0) ? 'Enter the amount the client disputes.'
      : f.response === 'DISPUTED' && (f.disputed ?? 0) > disputeCapOf(s) ? disputeCapText(s) : null,
  }), [f, s])
  return { f, set, errs }
}
const AnswerInputs: React.FC<{ s: StatementSummary; id: string; f: AnswerFields; set: ReturnType<typeof useAnswerFields>['set']; errs: ReturnType<typeof useAnswerFields>['errs']; tried: boolean; bad: ReturnType<typeof useBadFields>; dateLabel: string }> = ({ s, id, f, set, errs, tried, bad, dateLabel }) => {
  const held = f.response === 'DISPUTED' ? Math.min(f.disputed ?? 0, s.figures.totalDeductionsPence) : 0
  const showMethodErr = tried && !!errs.method
  return (
    <div className="grid gap-[12px] sm:grid-cols-2">
      <FormField label="How they answered" required variant="info" htmlFor={`${id}-method`}>
        <select id={`${id}-method`} className={SELECT} value={f.method} onChange={(e) => set('method', e.target.value as ClientApprovalMethod)}
          aria-invalid={showMethodErr || undefined} aria-describedby={showMethodErr ? `${id}-method-err` : undefined}>
          <option value="">Choose…</option>
          {MANUAL_METHODS.map((m) => <option key={m} value={m}>{CLIENT_APPROVAL_METHOD_LABELS[m]}</option>)}
        </select>
        <span id={`${id}-method-err`}>{tried && <FieldError message={errs.method ?? undefined} />}</span>
      </FormField>
      <FormField label="Date" required variant="info">
        <DateInput value={f.date} onChange={(v) => set('date', v)} ariaLabel={dateLabel} allowFuture={false} />
        {tried && <FieldError message={errs.date ?? undefined} />}
      </FormField>
      <fieldset className="m-0 p-0 border-0 sm:col-span-2">
        <legend className={LABEL}>Their answer</legend>
        <div className="flex gap-[14px] h-[38px] items-center text-[13.5px]">
          <label className="inline-flex items-center gap-[6px]"><input type="radio" name={`${id}-resp`} checked={f.response === 'AGREED'} onChange={() => set('response', 'AGREED')} />Agreed</label>
          <label className="inline-flex items-center gap-[6px]"><input type="radio" name={`${id}-resp`} checked={f.response === 'DISPUTED'} onChange={() => set('response', 'DISPUTED')} />Disputed</label>
        </div>
        {tried && <FieldError message={errs.response ?? undefined} />}
      </fieldset>
      {f.response === 'DISPUTED' && (
        <>
          <div>
            <PenceField label="Amount disputed" pence={f.disputed} allowBlank onPence={(v) => set('disputed', v)} onBadChange={bad.track('disputed')} />
            {tried && errs.disputed && <span className={FIELD_ERROR} role="alert">{errs.disputed}</span>}
          </div>
          <div>
            <FormField label="The client’s comment" variant="info" htmlFor={`${id}-comment`}>
              <input id={`${id}-comment`} className={INPUT} value={f.comment} maxLength={2000} onChange={(e) => set('comment', e.target.value)} />
            </FormField>
          </div>
          <div className={`sm:col-span-2 ${FIGURE_GRID}`} aria-label="What the dispute means">
            <Figure label="Paid to the client now" pence={s.figures.netToClientPence} />
            <Figure label="Held in client account" pence={held} />
            <Figure label="Undisputed deductions" pence={s.figures.totalDeductionsPence - held} />
          </div>
        </>
      )}
    </div>
  )
}
const answerBody = (s: StatementSummary, f: AnswerFields): ClientApprovalBody => ({
  statementVersion: s.version, contentHash: s.contentHash, method: f.method as ClientApprovalMethod, date: f.date as string,
  response: f.response as 'AGREED' | 'DISPUTED', disputedPence: f.response === 'DISPUTED' ? f.disputed : null, comment: f.comment,
})

// A form's own buttons: a row here, or loose among the main button row's items.
const FormButtons: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="flex items-center gap-[10px] flex-wrap">{children}</div>

// ── The manual answer form ────────────────────────────────────
export const ClientApprovalForm: React.FC<{ s: StatementSummary; today: string; busy: string | null; onSubmit: (body: ClientApprovalBody) => Promise<boolean>; onCancel?: () => void; caseId?: string; defaultMethod?: ClientApprovalMethod | ''; submitLabel?: string; mainSlot?: MainSlot; quiet?: boolean }> = ({ s, today, busy, onSubmit, onCancel, caseId, defaultMethod = '', submitLabel = 'Record the client’s answer', mainSlot, quiet = false }) => {
  const id = useId()
  const a = s.clientResponse
  const start: AnswerFields = { method: a?.method && a.method !== 'E_SIGNATURE' ? a.method : defaultMethod, date: a?.date ?? today, response: a?.response ?? '', disputed: a?.disputedPence ?? null, comment: a?.comment ?? '' }
  const { f, set, errs } = useAnswerFields(s, start)
  const [fileId, setFileId] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const bad = useBadFields()
  const dirty = (Object.keys(start) as (keyof AnswerFields)[]).some((k) => f[k] !== start[k]) || !!fileId
  const guard = useUnsavedChangesGuard(dirty)
  return (
    <div className="flex flex-col gap-3">
      <AnswerInputs s={s} id={id} f={f} set={set} errs={errs} tried={tried} bad={bad} dateLabel="Date the client answered" />
      {caseId && <AttachmentField caseId={caseId} value={fileId} onChange={setFileId} label="Attachment (optional)" />}
      {inSlot(mainSlot, <FormButtons>
        <button type="button" className={quiet ? B_SECONDARY : B_PRIMARY} data-main-action={quiet ? undefined : 'answer'} disabled={!!busy || bad.any} onClick={() => {
          setTried(true)
          if (errs.response || errs.method || errs.date || errs.disputed || bad.any) return
          void onSubmit({ ...answerBody(s, f), ...(fileId ? { fileId } : {}) })
        }}>{busy === 'approval' ? 'Saving…' : submitLabel}</button>
        {onCancel && <button type="button" className={B_SECONDARY} onClick={() => guard(onCancel)}>Cancel</button>}
        {bad.any && <span className={NOTE}>Correct the amount first.</span>}
      </FormButtons>)}
    </div>
  )
}

// ── The later-instruction form (after an e-signed answer) ─────
export const SELF_APPROVED = 'Self-approved — only approver at the firm'
export const NO_APPROVER = 'No one in the firm can approve this yet. An admin can give a colleague “Approve bills” in Admin › Permissions.'
export const LaterInstructionForm: React.FC<{ s: StatementSummary; today: string; busy: string | null; caseId: string; onSubmit: (body: LaterInstructionBody) => Promise<boolean>; onCancel: () => void; soleApprover?: boolean; noApprover?: boolean; checksOff?: boolean; mainSlot?: MainSlot; quiet?: boolean }> = ({ s, today, busy, caseId, onSubmit, onCancel, soleApprover, noApprover, checksOff, mainSlot, quiet = false }) => {
  const id = useId()
  const start: AnswerFields = { method: '', date: today, response: '', disputed: null, comment: '' }
  const { f, set, errs } = useAnswerFields(s, start)
  const [reason, setReason] = useState('')
  const [fileId, setFileId] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const bad = useBadFields()
  const dirty = (Object.keys(start) as (keyof AnswerFields)[]).some((k) => f[k] !== start[k]) || !!reason.trim() || !!fileId
  const guard = useUnsavedChangesGuard(dirty)
  const reasonErr = !reason.trim() ? 'Say why this later instruction is being recorded.' : null
  const fileErr = !fileId ? 'Attach the client’s signed form, email or letter.' : null
  // A refused submit lands on what's wrong, in form order (off-screen at 360 px otherwise).
  const formRef = useRef<HTMLElement>(null)
  const focusFirstInvalid = (): void => {
    window.setTimeout(() => {
      const root = formRef.current
      const first = errs.method ? root?.querySelector<HTMLElement>('select')
        : errs.date ? root?.querySelector<HTMLElement>('input:not([type="radio"])')
        : errs.response ? root?.querySelector<HTMLElement>('input[type="radio"]')
        : errs.disputed || bad.any ? root?.querySelector<HTMLElement>('[aria-invalid="true"], input[inputmode="decimal"]')
        : reasonErr ? root?.querySelector<HTMLElement>('textarea')
        : root?.querySelector<HTMLElement>('[data-attachment-field] button, [data-attachment-field] input')
      first?.focus()
    }, 0)
  }
  return (
    <section ref={formRef} aria-label="Record a later instruction" className="rounded-[12px] border border-casevo-border px-[14px] py-[12px] flex flex-col gap-3">
      <div>
        <h6 className="m-0 text-[13.5px] font-semibold text-casevo-forest">Record a later instruction</h6>
        <p className={`${NOTE} m-0 mt-[2px]`}>The e-signed answer stays on record. This instruction is kept alongside it and only counts once {soleApprover ? 'you approve it' : 'an approver — not you — approves it'}.</p>
      </div>
      <AnswerInputs s={s} id={id} f={f} set={set} errs={errs} tried={tried} bad={bad} dateLabel="Date the client gave the instruction" />
      <FormField label="Why it’s being recorded" required variant="info" htmlFor={`${id}-reason`}>
        <textarea id={`${id}-reason`} className={`${INPUT} h-auto min-h-[76px] py-2`} value={reason} maxLength={LATER_REASON_MAX} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. The client wrote on 2 November disputing the ATE premium."
          aria-invalid={(tried && !!reasonErr) || undefined} aria-describedby={`${id}-reason-count${tried && reasonErr ? ` ${id}-reason-err` : ''}`} />
        <span id={`${id}-reason-err`}>{tried && <FieldError message={reasonErr ?? undefined} />}</span>
        <span id={`${id}-reason-count`} className={`${NOTE} block mt-[4px]`}>{reason.length} / {LATER_REASON_MAX}</span>
      </FormField>
      <AttachmentField caseId={caseId} value={fileId} onChange={setFileId} required error={tried ? fileErr : null} />
      {soleApprover && <p className={`${NOTE} m-0`} role="note">{checksOff ? 'Two-person checks are off at this firm' : 'You’re the firm’s only approver'}, so you can approve this yourself. It will show as “{SELF_APPROVED}”.</p>}
      {noApprover && <p className="m-0 text-[12.5px] text-casevo-warn" role="note">{NO_APPROVER}</p>}
      {inSlot(mainSlot, <FormButtons>
        <button type="button" className={quiet ? B_SECONDARY : B_PRIMARY} data-main-action={quiet ? undefined : 'laterSend'} disabled={!!busy || bad.any || !!noApprover} onClick={() => {
          setTried(true)
          if (errs.response || errs.method || errs.date || errs.disputed || reasonErr || fileErr || bad.any) { focusFirstInvalid(); return }
          void onSubmit({ ...answerBody(s, f), reason: reason.trim(), fileId: fileId as string })
        }}>{busy === 'later' ? 'Saving…' : soleApprover ? 'Record it' : 'Send for approval'}</button>
        <button type="button" className={B_SECONDARY} onClick={() => guard(onCancel)}>Cancel</button>
        {bad.any && <span className={NOTE}>Correct the amount first.</span>}
      </FormButtons>)}
    </section>
  )
}

// ── Refuse a later instruction: the reason is required ────────
const RefuseDialog: React.FC<{ busy: boolean; onClose: () => void; onRefuse: (reason: string) => Promise<boolean> }> = ({ busy, onClose, onRefuse }) => {
  const id = useId()
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const guard = useUnsavedChangesGuard(reason.trim() !== '')
  const err = !reason.trim() ? 'Say why you are refusing it.' : null
  const close = (): void => guard(onClose)
  return (
    <ModalFrame size="sm" onClose={close} eyebrow="Later instruction" eyebrowIcon={<BIcon name="x" size={12} />} title="Refuse this later instruction?"
      subtitle="The money keeps following the client’s current answer. The person who recorded it is told why."
      footerRight={<><button type="button" className={B_SECONDARY} onClick={close}>Cancel</button>
        <button type="button" className={B_PRIMARY} disabled={busy} onClick={() => { setTried(true); if (!err) void onRefuse(reason.trim()).then((ok) => { if (ok) onClose() }) }}>{busy ? 'Refusing…' : 'Refuse'}</button></>}>
      <FormField label="Why" required variant="info" htmlFor={`${id}-why`}>
        <textarea id={`${id}-why`} className={`${INPUT} h-auto min-h-[96px] py-2`} value={reason} maxLength={LATER_REASON_MAX} onChange={(e) => setReason(e.target.value)}
          aria-invalid={(tried && !!err) || undefined} aria-describedby={tried && err ? `${id}-err` : undefined} />
        <span id={`${id}-err`}>{tried && <FieldError message={err ?? undefined} />}</span>
      </FormField>
    </ModalFrame>
  )
}

// ── The answer history (newest first) ─────────────────────────
const DECISION_CHIP: Record<string, { label: string; cls: string }> = {
  CURRENT: { label: 'Current', cls: 'border-casevo-light bg-casevo-mint text-casevo-brand' },
  PENDING: { label: 'Waiting for approval', cls: 'border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn' },
  REFUSED: { label: 'Refused', cls: 'border-casevo-border bg-casevo-surface text-casevo-text-soft' },
  EARLIER: { label: 'Earlier', cls: 'border-casevo-border bg-casevo-surface text-casevo-text-soft' },
}
const chipOf = (e: AnswerHistoryEntry): string => (e.current ? 'CURRENT' : e.decision === 'PENDING' ? 'PENDING' : e.decision === 'REFUSED' ? 'REFUSED' : 'EARLIER')
const titleOf = (e: AnswerHistoryEntry): string =>
  e.kind === 'LATER_INSTRUCTION' ? 'Later instruction' : e.method === 'E_SIGNATURE' ? 'Signed online' : 'Answer recorded'
export const AnswerHistory: React.FC<{ entries: AnswerHistoryEntry[]; caseId?: string }> = ({ entries, caseId }) => (
  <div aria-label="Answer history">
    <h6 className="m-0 mb-[6px] text-[12px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft">Answer history</h6>
    <ol className="m-0 p-0 list-none flex flex-col divide-y divide-casevo-border border border-casevo-border rounded-[10px]">
      {entries.map((e) => {
        const chip = DECISION_CHIP[chipOf(e)]
        return (
          <li key={e.id} className="px-[12px] py-[9px] flex flex-col gap-[3px] text-[13px]" data-history={e.kind} data-decision={e.decision ?? ''}>
            <div className="flex items-center gap-[8px] flex-wrap">
              <b className="font-semibold text-casevo-forest">{titleOf(e)}</b>
              <span className={`${CHIP} ${chip.cls}`}>{chip.label}</span>
              <span className="text-casevo-forest">{answerText(e.response, e.disputedPence)}</span>
            </div>
            <span className="text-[12.5px] text-casevo-text-soft">
              {e.method ? CLIENT_APPROVAL_METHOD_LABELS[e.method] : '—'}{e.date ? ` · given ${day(e.date)}` : ''} · {e.method === 'E_SIGNATURE' ? 'recorded automatically' : `recorded by ${e.recordedBy}`} on {day(e.recordedAt)}
            </span>
            {e.kind === 'LATER_INSTRUCTION' && e.reason && <span className="text-[12.5px] text-casevo-forest">Reason: ‘{e.reason}’</span>}
            {e.comment && <span className="text-[12.5px] text-casevo-forest">Client’s comment: “{e.comment}”</span>}
            {e.decision === 'APPROVED' && <span className="text-[12.5px] text-casevo-forest">{e.selfNote ? `${e.selfNote}: ${e.decidedBy ?? 'a former member'}` : e.selfApproved ? `${SELF_APPROVED}: ${e.decidedBy ?? 'a former member'}` : `Approved by ${e.decidedBy ?? 'a former member'}`} on {day(e.decidedAt)}</span>}
            {e.decision === 'REFUSED' && <span className="text-[12.5px] text-casevo-forest">Refused by {e.decidedBy ?? 'a former member'} on {day(e.decidedAt)}{e.refusalReason ? ` — ‘${e.refusalReason}’` : ''}</span>}
            {e.file && <span><FileLink caseId={caseId} file={e.file} /></span>}
          </li>
        )
      })}
    </ol>
  </div>
)

// ── The client's answer (adults): button → form → summary ────
// (Follow-ups) The client can only answer a statement they've received: until
// its delivery (email, post or hand) is recorded the answer can't be recorded
// (server-enforced too) — said in visible text, with a button to record it.
export const ClientAnswer: React.FC<{
  s: StatementSummary; today: string; busy: string | null; onSubmit: (body: ClientApprovalBody) => Promise<boolean>; onRecordDelivery?: () => void
  // (M1) The later-instruction flow + who is looking.
  caseId?: string; later?: LaterActions; viewerUserId?: string; canCheck?: boolean; onlyApprover?: boolean; noApprover?: boolean; checksOff?: boolean
  // (Settlement redesign) This is the current step: the form is already open, its
  // method filled in from how the statement went out. History shows elsewhere.
  openAtStep?: boolean; defaultMethod?: ClientApprovalMethod | ''; historyElsewhere?: boolean; mainLater?: boolean
  // The answer's done: it folds to one line (the money column has the figures).
  foldSummary?: boolean
  mainChange?: boolean
  // On the statement card, the main button is in the card's own spot, and until the
  // statement is sent there's nothing to show here: that step is the main button.
  mainSlot?: MainSlot
  // A newer draft has the main button: everything here stays secondary.
  quiet?: boolean
}> = ({ s, today, busy, onSubmit, onRecordDelivery, caseId, later, viewerUserId, canCheck = false, onlyApprover = false, noApprover = false, checksOff = false, openAtStep = false, defaultMethod = '', historyElsewhere = false, mainLater = false, foldSummary = false, mainChange = false, mainSlot, quiet = false }) => {
  const [open, setOpen] = useState(false)
  const [laterOpen, setLaterOpen] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [refuseId, setRefuseId] = useState<string | null>(null)
  const a = s.clientResponse
  const history = s.answerHistory ?? []
  const current = history.find((e) => e.current) ?? null
  const pending = history.find((e) => e.decision === 'PENDING') ?? null
  const carried = s.carriedDispute ?? null
  const locked = !!a?.esignLocked || !!carried
  const notDelivered = !s.delivered
  const onCard = mainSlot !== undefined
  const mainBtn = (on: boolean): string => (on && !quiet ? B_PRIMARY : B_SECONDARY_SM)
  const needsDelivery = notDelivered && !onCard && (
    // (Audit fix batch 2) The single-notice shell: the fact, then the one action bottom-right.
    <div data-answer-blocked="">
      <ActionBox ariaLabel="Not delivered yet" title="Not delivered yet" message={ANSWER_NEEDS_DELIVERY}
        actions={onRecordDelivery ? [{ label: 'Record delivery…', kind: 'primary', icon: 'send', disabled: !!busy, onClick: onRecordDelivery }] : undefined} />
    </div>
  )
  const heldNow = a?.response === 'DISPUTED' ? Math.min(a.disputedPence ?? 0, s.figures.totalDeductionsPence) : 0
  const held = s.position?.heldPence ?? heldNow
  const agreed = s.position?.transferableDeductionsPence ?? s.figures.totalDeductionsPence - held
  const paid = s.position?.payableToClientPence ?? s.figures.netToClientPence
  // The approver of a pending instruction: an approver who didn't record it (the server checks too).
  const canDecide = !!pending && !!later && canCheck && !!viewerUserId && (pending.recordedByUserId !== viewerUserId || onlyApprover || checksOff)
  const pendingBox = pending && (
      <div data-pending-instruction="">
        <ActionBox ariaLabel="Later instruction waiting for approval" tone="warn" title="Later instruction waiting for approval"
          message={<>
            <span className="block">{answerText(pending.response, pending.disputedPence)} — {pending.method ? CLIENT_APPROVAL_METHOD_LABELS[pending.method] : ''}{pending.date ? `, given ${day(pending.date)}` : ''}. Recorded by {pending.recordedBy} on {day(pending.recordedAt)}.</span>
            {pending.reason && <span className="block mt-[4px]">Reason: ‘{pending.reason}’</span>}
            {pending.file && <span className="block mt-[4px]"><FileLink caseId={caseId} file={pending.file} /></span>}
            <span className="block mt-[4px]">{canDecide ? 'You can approve this. Check the attachment first — once approved, the money follows it.' : `Waiting for an approver other than ${pending.recordedByUserId === viewerUserId ? 'you' : pending.recordedBy}. Until then the money follows the e-signed answer.`}</span>
          </>}
          actions={canDecide ? [
            { label: 'Refuse…', kind: 'link', disabled: !!busy, onClick: () => setRefuseId(pending.id) },
            ...(onCard ? [] : [{ label: 'Approve', kind: 'primary' as const, disabled: !!busy, busy: busy === 'laterApprove', busyLabel: 'Approving…', onClick: () => setConfirmId(pending.id) }]),
          ] : undefined} />
      </div>
  )
  const laterButton = (disabled: boolean): React.ReactNode => {
    const button = <button type="button" className={mainBtn(mainLater)} data-main-action={mainLater && !quiet ? 'later' : undefined} disabled={disabled} onClick={() => setLaterOpen(true)}><BIcon name="plus" size={12} />Record a later instruction</button>
    return mainLater ? inSlot(mainSlot, button) : button
  }
  const showHistory = history.length > 1 || history.some((e) => e.kind === 'LATER_INSTRUCTION' || !!e.file)
  return (
    <div aria-label="Client’s answer" className="flex flex-col gap-3 empty:hidden">
      {!(foldSummary && a) && !(onCard && notDelivered) && <h5 className="m-0 text-[13.5px] font-semibold text-casevo-forest">Client’s answer to version {s.version}</h5>}
      {needsDelivery}
      {carried && !a && <CarriedDisputeNote d={carried} />}
      {(open || (openAtStep && !a && !carried)) && !notDelivered && !locked ? (
        <ClientApprovalForm s={s} today={today} busy={busy} caseId={caseId} onCancel={open ? () => setOpen(false) : undefined} defaultMethod={defaultMethod} submitLabel={openAtStep ? 'Record the answer' : undefined} mainSlot={mainSlot} quiet={quiet}
          onSubmit={async (body) => { const ok = await onSubmit(body); if (ok) setOpen(false); return ok }} />
      ) : laterOpen && locked && later && caseId ? (
        <LaterInstructionForm s={s} today={today} busy={busy} caseId={caseId} onCancel={() => setLaterOpen(false)}
          soleApprover={(onlyApprover || checksOff) && canCheck} checksOff={checksOff} noApprover={noApprover} mainSlot={mainSlot} quiet={quiet}
          onSubmit={async (body) => { const ok = await later.record(body); if (ok) setLaterOpen(false); return ok }} />
      ) : a ? (
        <div className="flex flex-col gap-[8px]">
          {/* (Settlement redesign) Folded once it's done. */}
          {((summary: React.ReactNode): React.ReactNode => (foldSummary
            ? <Fold className="rounded-[10px] border border-casevo-border-soft px-[12px] py-[8px]" labelClass="text-[13px] text-casevo-forest" dataKey="answer-summary"
                label={<><span className="text-casevo-brand dark:text-casevo-link">✓</span> {answerText(a.response, a.disputedPence)} · {a.method ? CLIENT_APPROVAL_METHOD_LABELS[a.method] : '—'} · {a.date ? ukDate(a.date) : '—'}</>}>{summary}</Fold>
            : summary))(<>
          <dl className="m-0 grid gap-[10px] sm:grid-cols-3">
            <SummaryRow label="Answer">{answerText(a.response, a.disputedPence)}</SummaryRow>
            <SummaryRow label="How">{a.method ? CLIENT_APPROVAL_METHOD_LABELS[a.method] : '—'}</SummaryRow>
            <SummaryRow label="Date">{a.date ? ukDate(a.date) : '—'}</SummaryRow>
            {current && (
              <div className="sm:col-span-3"><SummaryRow label="Recorded">
                {current.kind === 'LATER_INSTRUCTION'
                  ? `A later instruction recorded by ${current.recordedBy} on ${day(current.recordedAt)}, approved by ${current.decidedBy ?? 'a former member'} on ${day(current.decidedAt)}`
                  : current.method === 'E_SIGNATURE' ? `Automatically, when the client signed online on ${day(current.recordedAt)}` : `By ${current.recordedBy} on ${day(current.recordedAt)}`}
              </SummaryRow></div>
            )}
            {a.comment && <div className="sm:col-span-3"><SummaryRow label="Comment">“{a.comment}”</SummaryRow></div>}
          </dl>
          {!foldSummary && (
            <div className={FIGURE_GRID} aria-label="What the answer means">
              <Figure label="Paid now" pence={paid} />
              <Figure label="Held (disputed)" pence={held} />
              <Figure label="Agreed deductions" pence={agreed} />
            </div>
          )}
          </>)}
          {pendingBox}
          {locked ? (
            <div className="flex flex-col gap-[6px]">
              <p className={`${NOTE} m-0`}>{current?.kind === 'LATER_INSTRUCTION' ? ESIGN_LOCKED_NOTE_AFTER : ESIGN_LOCKED_NOTE}</p>
              {later && caseId && (
                <div className="flex items-center gap-[8px] flex-wrap">
                  {laterButton(!!busy || !!pending)}
                  {pending && <span className={NOTE}>Approve or refuse the one waiting first.</span>}
                </div>
              )}
            </div>
          ) : (
            // Agreed and done, it's a quiet link beside the others: nothing here needs doing.
            !mainChange && onCard
              ? inSlot(mainSlot, <button type="button" className={QUIET} data-change-answer="" disabled={!!busy || notDelivered} onClick={() => setOpen(true)}>Change answer</button>)
              : ((button: React.ReactNode): React.ReactNode => (mainChange ? inSlot(mainSlot, button) : <div>{button}</div>))(<button type="button" className={mainBtn(mainChange)} data-main-action={mainChange && !quiet ? 'change' : undefined} disabled={!!busy || notDelivered} onClick={() => setOpen(true)}>Change answer</button>)
          )}
        </div>
      ) : carried ? (
        <div className="flex flex-col gap-[8px]">
          {pendingBox}
          {later && caseId && !(onCard && notDelivered) && (
            <div className="flex items-center gap-[8px] flex-wrap">
              {laterButton(!!busy || !!pending || notDelivered)}
              {pending && <span className={NOTE}>Approve or refuse the one waiting first.</span>}
            </div>
          )}
        </div>
      ) : onCard ? null : (
        <div><button type="button" className={B_SECONDARY} disabled={!!busy || notDelivered} onClick={() => setOpen(true)}>Record the client’s answer</button></div>
      )}
      {canDecide && onCard && pending && !open && !laterOpen && inSlot(mainSlot, <button type="button" className={quiet ? B_SECONDARY : B_PRIMARY} data-main-action={quiet ? undefined : 'laterApprove'} disabled={!!busy} aria-busy={busy === 'laterApprove' || undefined} onClick={() => setConfirmId(pending.id)}>{busy === 'laterApprove' ? 'Approving…' : 'Approve'}</button>)}
      {showHistory && !historyElsewhere && !open && !laterOpen && <AnswerHistory entries={history} caseId={caseId} />}
      {confirmId && later && (
        <ConfirmDialogue isOpen title="Approve this later instruction?" confirmLabel="Approve" tone="primary"
          message="You confirm you have checked the attachment and that it is the client’s instruction. The money will follow it; the e-signed answer stays on record."
          onCancel={() => setConfirmId(null)}
          onConfirm={() => { const insId = confirmId; setConfirmId(null); void later.approve(insId) }} />
      )}
      {refuseId && later && <RefuseDialog busy={busy === 'laterRefuse'} onClose={() => setRefuseId(null)} onRefuse={(r) => later.refuse(refuseId, r)} />}
    </div>
  )
}
